<template>
  <div :class="disabledDiv(disabled)" class="*:grow-0!">
    <!-- Complex Collection Attribute -->
    <div v-if="modelValue?.data_type === 'complex_collection'" :class="disabledDiv(disabled)">
      <v-select
        v-model="options.key"
        variant="outlined"
        density="compact"
        persistentPlaceholder
        hideDetails
        :items="Object.keys(modelValue.options.collection_schema)"
        :disabled="disabled"
        placeholder="Attribute"
        bgColor="#ffffff"
        style="min-width: 180px"
        :menuProps="{ maxHeight: 410, offsetY: true, minWidth: 240 }"
      />
      <v-icon>md:chevron_right</v-icon>
    </div>
    <!-- Include / Exclude -->
    <v-select
      v-if="modelValue?.data_type === 'complex_collection' ? options.key : true"
      v-model="options.negative_match"
      class="min-w-28"
      variant="outlined"
      density="compact"
      :disabled="disabled"
      persistentPlaceholder
      hideDetails
      :items="matchTypeItems"
      height="32px"
      itemTitle="text"
      placeholder="Match Type"
      bgColor="#ffffff"
    />
    <!-- Date Type -->
    <div
      v-if="matchTypeSelected && model.display_type === 'date_range'"
      :class="disabledDiv(disabled)"
    >
      <v-icon>md:chevron_right</v-icon>
      <v-select
        v-model="options.date_type"
        variant="outlined"
        density="compact"
        persistentPlaceholder
        hideDetails
        itemTitle="text"
        bgColor="#ffffff"
        placeholder="Date Type"
        height="32px"
        class="min-w-28"
        :disabled="disabled"
        :items="dateTypeOptions"
      />
    </div>
    <!-- Matcher Type -->
    <div v-if="matchTypeSelected && matcherTypeOptions" :class="disabledDiv(disabled)">
      <v-icon>md:chevron_right</v-icon>
      <v-select
        v-model="options[model.data_type === 'date' ? 'range_type' : 'matcher_type']"
        variant="outlined"
        density="compact"
        persistentPlaceholder
        hideDetails
        bgColor="#ffffff"
        placeholder="Matcher Type"
        itemTitle="text"
        height="32px"
        class="w-44"
        :disabled="disabled"
        :valueComparator="(a, b) => a === b || (!a && !b)"
        :items="matcherTypeOptions"
      />
      <v-tooltip
        v-if="
          model.data_type === 'string' &&
          (model.display_type === 'text_input' ||
            (model.display_type === 'multi_select' && matcherType)) &&
          matcherType !== 'values_in'
        "
        location="bottom"
      >
        <template #activator="{ props }">
          <v-icon class="ml-1" color="#888" size="16" v-bind="props">md:info</v-icon>
        </template>
        <div>Text comparison is case insensitive</div>
      </v-tooltip>
      <v-tooltip v-if="rangeType === 'less_than_or_equal_to'" bottom>
        <template #activator="{ props }">
          <v-icon class="ml-1" color="#888" size="16" v-bind="props">md:info</v-icon>
        </template>
        <div>
          "Today" is included, while
          {{ options.relative_date_type === "days_ago" ? "future" : "past" }} dates are excluded
        </div>
      </v-tooltip>
    </div>
    <!-- Custom -->
    <v-icon v-if="matchTypeSelected && (model.display_type !== 'date_range' || dateTypeSelected)">
      md:chevron_right
    </v-icon>
    <!-- Custom: values_in -->
    <div v-if="matcherType === 'values_in' || rangeType === 'values_in'" class="flex">
      <v-text-field
        v-model.trim="inputValueSet"
        variant="outlined"
        density="compact"
        hideDetails
        persistentPlaceholder
        placeholder="Values list"
        hint="Hello world"
        :disabled="disabled"
        class="w-60"
        bgColor="#ffffff"
      />
      <v-tooltip bottom>
        <template #activator="{ props }">
          <v-icon class="mr-2 ml-1 self-center" color="#888" size="16" v-bind="props"
            >md:info</v-icon
          >
        </template>
        <div class="values-in-tooltip">
          <strong>"Values in" Format</strong>
          <template v-if="model.data_type === 'string'">
            <span>e.g. <code>NULL,some text,"AbC"</code></span>
            <span class="mt-2"> Matches if any condition is true: </span>
            <span> <code>NULL</code> &ndash; value is not set or blank string <code>""</code></span>
            <span>
              <code>some text</code> &ndash; value is entirely
              <code>some text</code> (case-insensitive)
            </span>
            <span>
              <code>"AbC"</code> &ndash; value is entirely and exactly
              <code>AbC</code> (case-sensitive)
            </span>
          </template>
          <template v-else>
            <span>e.g. <code>NULL,5,6,9...12,...3,20...</code></span>
            <span class="mt-2"> Matches if any condition is true: </span>
            <span> <code>NULL</code> &ndash; value is not set </span>
            <span> <code>5,6</code> &ndash; value is 5 or 6 </span>
            <span> <code>9...12</code> &ndash; value is between 9 and 12 (inclusive) </span>
            <span> <code>...3</code> &ndash; value is 3 or less </span>
            <span> <code>20...</code> &ndash; value is 20 or greater </span>
          </template>
        </div>
      </v-tooltip>
    </div>
    <!-- Custom: slider -->
    <div
      v-else-if="model.display_type === 'slider' && matchTypeSelected"
      :class="disabledDiv(disabled)"
    >
      <RfCustomControlsSlider v-model="valueSet" :model="model" :disabled="disabled" />
    </div>
    <!-- Custom: range_select -->
    <div
      v-else-if="model.display_type === 'range_select' && matchTypeSelected"
      :class="disabledDiv(disabled)"
    >
      <RfCustomRangeSelector
        v-if="!matcherType"
        v-model="valueSet"
        class="min-w-42"
        :model="model"
        :disabled="disabled"
      />
    </div>
    <!-- Custom: multi_select -->
    <div
      v-else-if="model.display_type === 'multi_select' && matchTypeSelected && !matcherType"
      :class="disabledDiv(disabled)"
    >
      <!-- Show autocomplete for null matcher type (default behavior) -->
      <v-autocomplete
        v-model="valueSet"
        variant="outlined"
        density="compact"
        persistentPlaceholder
        hideDetails
        chips
        multiple
        itemTitle="text"
        itemValue="value"
        :disabled="disabled"
        :items="model.data_type === 'boolean' ? booleanValues : model.values"
        :placeholder="model.name"
        bgColor="#ffffff"
        style="min-width: 150px"
        :valueComparator="vSelectMatch"
        :menuProps="{ maxHeight: 410, offsetY: true, minWidth: 200 }"
      >
        <template #item="{ item, props: itemProps }">
          <v-tooltip location="bottom">
            <template #activator="{ props }">
              <v-list-item v-bind="{ ...props, ...itemProps }">
                <template #prepend="{ isSelected }">
                  <v-checkbox
                    hideDetails
                    class="ml-n2 mt-0 pt-0"
                    readonly
                    :modelValue="isSelected"
                  />
                </template>
              </v-list-item>
            </template>
            <span>{{ item?.raw?.text || item?.raw || item }}</span>
          </v-tooltip>
        </template>
      </v-autocomplete>
    </div>
    <!-- Custom: text_input (also handles multi_select with advanced matcher types) -->
    <div v-else-if="shouldShowTextInput" :class="disabledDiv(disabled)">
      <v-text-field
        v-model.trim="inputValueSet"
        class="min-w-60"
        variant="outlined"
        density="compact"
        hideDetails
        persistentPlaceholder
        :placeholder="model.name"
        :hint="model.description"
        :disabled="disabled"
        bgColor="#ffffff"
        height="32px"
      ></v-text-field>
    </div>
    <!-- Custom: date_range -->
    <div
      v-if="model.display_type === 'date_range' && dateTypeSelected && matchTypeSelected"
      :class="disabledDiv(disabled)"
    >
      <div v-if="isDayRange && rangeType !== 'values_in'" style="display: inherit">
        <v-text-field
          v-if="minValAvail"
          v-model="valueSet[0]"
          class="min-w-16"
          variant="outlined"
          :disabled="disabled"
          density="compact"
          hideDetails
          persistentPlaceholder
          placeholder="Days"
          bgColor="#ffffff"
          :style="dateStyles"
        />
        <span v-if="rangeType === 'range'" style="margin-right: 8px; padding-top: 8px">to: </span>
        <v-text-field
          v-if="maxValAvail"
          v-model="valueSet[1]"
          class="min-w-16"
          variant="outlined"
          :disabled="disabled"
          density="compact"
          hideDetails
          persistentPlaceholder
          placeholder="Days"
          bgColor="#ffffff"
          :style="dateStyles"
        />
      </div>
      <v-select
        v-if="isDayRange"
        v-model="options.relative_date_type"
        variant="outlined"
        persistentPlaceholder
        itemTitle="text"
        density="compact"
        hideDetails
        bgColor="#ffffff"
        height="32px"
        class="w-36 min-w-36"
        :items="relativeDateTypeOptions"
        :disabled="disabled"
        :style="dateStyles"
      />
      <div>
        <RfStartStopDatePicker
          v-if="!isDayRange"
          ref="dateRangePicker"
          :dateRange="dateRange"
          :disabled="disabled"
          showClearIcon
          hideTime
          @dateRangeUpdate="dateRangeUpdate"
        />
      </div>
    </div>
    <!-- Custom: multi_input -->
    <div
      v-if="matchTypeSelected && model.display_type === 'multi_input'"
      :class="disabledDiv(disabled)"
    >
      <RfMultiChipInput
        v-model="valueSet"
        class="min-w-56"
        placeholder="Enter values (case-sensitive)"
        :disabled="disabled"
        @update:modelValue="updateValues"
      />
    </div>
    <RfButtonNew
      v-if="!isLastControl"
      size="none"
      type="plain"
      class="ml-2 shrink-0"
      :disabled="disabled"
      @click="$emit('deleteRow')"
    >
      <RfCloseIcon class="fill-beige-50 h-6 w-6" />
    </RfButtonNew>
  </div>
</template>

<script>
import RfCustomControlsSlider from "@/components/RfCustomControlsSlider.vue";
import RfCustomRangeSelector from "@/components/RfCustomRangeSelector.vue";
import RfStartStopDatePicker from "@/components/RfStartStopDatePicker.vue";
import RfMultiChipInput from "@/components/RfMultiChipInput.vue";
import { MatchTypeItems } from "@/utils/constants/SegmentsConstants";
import { cloneDeep } from "lodash-es";
import RfButtonNew from "./buttons/RfButtonNew.vue";
import RfCloseIcon from "./icons/RfCloseIcon.vue";

export default {
  name: "RfCustomControls",
  components: {
    RfCustomControlsSlider,
    RfCustomRangeSelector,
    RfStartStopDatePicker,
    RfMultiChipInput,
    RfCloseIcon,
    RfButtonNew,
  },
  props: {
    modelValue: Object,
    values: Array,
    disabled: [Boolean, String],
    currentOptions: Object,
    isLastControl: { type: Boolean, default: false },
  },
  setup: () => ({ matchTypeItems: Object.values(MatchTypeItems) }),
  data() {
    const defaultOptions = {
      negative_match: null,
      range_type: null,
      date_type: null,
      relative_date_type: null,
      matcher_type: null,
    };

    return {
      valueSet: cloneDeep(this.values) || [],
      booleanValues: [
        { text: "Null (no value)", value: "NULL" },
        { text: "True", value: "1" },
        { text: "False", value: "0" },
      ],
      dateTypeOptions: [
        { text: "Day Range", value: "day_range" },
        { text: "Date Range", value: "date_range" },
      ],
      relativeDateTypeOptions: [
        { text: "Days Ago", value: "days_ago" },
        { text: "Days From Now", value: "days_from_now" },
      ],
      options: cloneDeep({ ...defaultOptions, ...this.currentOptions }),
    };
  },
  computed: {
    model() {
      return (
        (this.modelValue.data_type === "complex_collection"
          ? this.modelValue.options.collection_schema[this.options.key]
          : this.modelValue) || {}
      );
    },
    matcherTypeOptions() {
      const { data_type, display_type } = this.model;

      if (display_type === "multi_input") return null;

      const valuesInMatcher = { text: "Values In (advanced)", value: "values_in" };

      if (data_type === "string") {
        return [
          { text: display_type === "text_input" ? "Exact Text" : "Multi-Select", value: null },
          { text: "Wildcard", value: "wildcard" },
          { text: "Regular Expression", value: "regex" },
          valuesInMatcher,
        ];
      }
      if (data_type === "date" && this.isDayRange) {
        return [
          { text: "From To", value: "range" },
          { text: "Less than or equal to", value: "less_than_or_equal_to" },
          { text: "Greater than or equal to", value: "greater_than_or_equal_to" },
          valuesInMatcher,
        ];
      }

      if (data_type === "number" && ["text_input", "range_select"].includes(display_type)) {
        return [
          { text: display_type === "text_input" ? "Exact Number" : "From To", value: null },
          valuesInMatcher,
        ];
      }

      return null;
    },
    inputValueSet: {
      get() {
        return (this.valueSet && this.valueSet[0]) || [];
      },
      set(value) {
        if (!value.length) {
          this.valueSet = [];
        } else {
          this.valueSet = [value];
        }
      },
    },
    dateRange() {
      return {
        startDate: this.valueSet && this.valueSet[0],
        endDate: this.valueSet && this.valueSet[1],
      };
    },
    isDayRange() {
      return this.dateType === "day_range";
    },
    minValAvail() {
      return this.isDayRange && ["range", "greater_than_or_equal_to"].includes(this.rangeType);
    },
    maxValAvail() {
      return this.isDayRange && ["range", "less_than_or_equal_to"].includes(this.rangeType);
    },
    rangeType() {
      return this.options.range_type;
    },
    matcherType() {
      return this.options.matcher_type;
    },
    dateType() {
      return this.options.date_type;
    },
    dateStyles() {
      return { "margin-right": "8px" };
    },
    matchTypeSelected() {
      return this.options.negative_match !== null;
    },
    dateTypeSelected() {
      return !!this.options.date_type;
    },
    shouldShowTextInput() {
      return (
        (this.model.display_type === "text_input" && this.matchTypeSelected) ||
        (this.model.display_type === "multi_select" && this.matchTypeSelected && this.matcherType)
      );
    },
  },
  watch: {
    valueSet: {
      handler(to) {
        const options = Object.keys(this.options).reduce((opts, key) => {
          if (this.options[key] !== null) opts[key] = this.options[key];
          return opts;
        }, {});
        this.$emit("update", to, options);
      },
      deep: true,
    },
    options: {
      handler(to) {
        this.$emit("update", this.valueSet, to);
      },
      deep: true,
    },
    dateType(to) {
      if (to === "day_range") {
        this.options.range_type = "range";
        this.options.relative_date_type = "days_ago";
      } else {
        this.options.range_type = null;
        this.options.relative_date_type = null;
      }
      this.valueSet = [];
    },
    rangeType(to) {
      if (to === "less_than_or_equal_to") {
        this.valueSet[0] = 0;
        this.valueSet[1] = null;
      } else if (to === "greater_than_or_equal_to") {
        this.valueSet[1] = "INF";
        this.valueSet[0] = null;
      }
    },
    matcherType(to, from) {
      if (to === "values_in") {
        this.valueSet = [this.valueSet[0]];
      } else if (
        this.model.display_type === "multi_select" &&
        from !== to &&
        (from === null || to === null)
      ) {
        // Clear values when switching between Multi-Select and advanced matchers
        this.valueSet = [];
      }
    },
  },
  created() {
    if (this.currentOptions?.range_type === "less_than_or_equal_to") {
      this.valueSet[0] = 0;
    } else if (this.currentOptions?.range_type === "greater_than_or_equal_to") {
      this.valueSet[1] = "INF";
    }
  },
  methods: {
    vSelectMatch(value1, value2) {
      try {
        const escaped = value1.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, "\\$&");
        const regexValueCaseInsensitive = new RegExp(`^${escaped}\$`, "i");
        return regexValueCaseInsensitive.test(value2);
      } catch {
        return value1 === value2;
      }
    },
    disabledDiv(val) {
      return `flex items-center ${val ? "disabled-custom-field" : ""}`;
    },
    dateRangeUpdate(data) {
      this.valueSet = [data.startDate || "", data.endDate || ""];
    },
  },
};
</script>

<style scoped lang="scss">
.values-in-tooltip {
  display: flex;
  flex-direction: column;
  code {
    background: #000;
  }
}

:deep(.schedule-date-selector-wrapper) {
  > .container > .row {
    flex-direction: row;
  }
  .date-selector {
    flex: 1;
    display: flex;
    &:first-child {
      margin-right: 16px;
    }
    &:last-child:before {
      content: "to: ";
      margin: 6px 8px 0 -8px;
    }
  }
  .date-selector-text {
    display: none;
  }
}
</style>
